import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/founder", label: "Founder Office" },
];

const PORTFOLIO = [
  { to: "/portfolio", label: "Portfolio Overview", external: false },
  { to: "/steward-and-stone", label: "Steward & Stone", external: false },
  { to: "/strategic-assets", label: "Strategic Assets", external: false },
  { to: "https://nirvalife.com", label: "Nirva Life", external: true },
  { to: "/foundation", label: "Jennae Michelle Foundation", external: false },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const ddRef = useRef(null);
  const toggleRef = useRef(null);

  // Light pages (stone-white) need dark header text for legibility.
  const light = pathname === "/foundation";

  useEffect(() => {
    setOpen(false);
    setDdOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDdOpen(false);
        setOpen((wasOpen) => {
          if (wasOpen) toggleRef.current?.focus();
          return false;
        });
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const inPortfolio =
    pathname.startsWith("/portfolio") ||
    pathname.startsWith("/steward-and-stone") ||
    pathname.startsWith("/strategic-assets") ||
    pathname.startsWith("/foundation");

  const linkCls = (active) => {
    if (light) {
      return `font-display uppercase tracking-wide2 text-[11px] link-underline transition-colors ${active ? "text-[var(--gold-deep)]" : "text-[var(--ink)] hover:text-[var(--gold-deep)]"}`;
    }
    return `font-display uppercase tracking-wide2 text-[11px] link-underline transition-colors ${active ? "text-[var(--gold-lite)]" : "text-[var(--bone)] hover:text-[var(--gold-lite)]"}`;
  };

  const headerScrolled = light
    ? "bg-[rgba(243,240,231,0.92)] backdrop-blur-md border-b border-[rgba(180,139,63,0.3)]"
    : "bg-[rgba(8,9,11,0.86)] backdrop-blur-md border-b border-[rgba(180,139,63,0.16)]";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled || open ? headerScrolled : "bg-transparent"}`}
      data-testid="site-nav"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          className={`font-display uppercase tracking-wide2 text-sm ${light ? "text-[var(--ink)]" : "gold-text"}`}
          data-testid="nav-logo"
        >
          Jennae Michelle <span className="tracking-luxe text-[10px]">Holdings</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} className={linkCls(pathname === l.to)} data-testid={`nav-link-${slug(l.label)}`}>
              {l.label}
            </Link>
          ))}

          <div className="relative" ref={ddRef}>
            <button
              type="button"
              onClick={() => setDdOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={ddOpen}
              className={`flex items-center gap-1 ${linkCls(inPortfolio)}`}
              data-testid="nav-portfolio-trigger"
            >
              Portfolio
              <ChevronDown size={13} className={`transition-transform duration-300 ${ddOpen ? "rotate-180" : ""}`} />
            </button>
            {ddOpen && (
              <div
                className="absolute right-0 mt-4 min-w-[240px] bg-[rgba(8,9,11,0.98)] backdrop-blur-md border border-[rgba(180,139,63,0.22)] py-3 shadow-2xl"
                role="menu"
                data-testid="nav-portfolio-menu"
              >
                {PORTFOLIO.map((p) =>
                  p.external ? (
                    <a
                      key={p.to}
                      href={p.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className="block px-5 py-2.5 font-display uppercase tracking-wide2 text-[10px] text-[var(--bone)]/85 hover:text-[var(--gold-lite)] hover:bg-[rgba(180,139,63,0.06)] transition-colors"
                      data-testid={`nav-portfolio-${slug(p.label)}`}
                    >
                      {p.label} <span className="text-[var(--muted)]">↗</span>
                    </a>
                  ) : (
                    <Link
                      key={p.to}
                      to={p.to}
                      role="menuitem"
                      className={`block px-5 py-2.5 font-display uppercase tracking-wide2 text-[10px] transition-colors hover:bg-[rgba(180,139,63,0.06)] ${pathname === p.to ? "text-[var(--gold-lite)]" : "text-[var(--bone)]/85 hover:text-[var(--gold-lite)]"}`}
                      data-testid={`nav-portfolio-${slug(p.label)}`}
                    >
                      {p.label}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>

          <Link to="/contact" className={linkCls(pathname === "/contact")} data-testid="nav-link-contact">
            Contact
          </Link>
        </nav>

        <button
          ref={toggleRef}
          className={`md:hidden ${light ? "text-[var(--ink)]" : "text-[var(--gold-lite)]"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="nav-mobile-menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav id="nav-mobile-menu" className="md:hidden bg-[#08090b] border-t border-[rgba(180,139,63,0.16)] px-6 py-6 flex flex-col gap-5 shadow-2xl" aria-label="Mobile" data-testid="nav-mobile-menu">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} className="font-display uppercase tracking-wide2 text-sm text-[var(--bone)]" data-testid={`nav-mobile-${slug(l.label)}`}>
              {l.label}
            </Link>
          ))}
          <div className="pt-1">
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80 mb-3">Portfolio</p>
            <div className="flex flex-col gap-4 pl-3 border-l border-[rgba(180,139,63,0.22)]">
              {PORTFOLIO.map((p) =>
                p.external ? (
                  <a key={p.to} href={p.to} target="_blank" rel="noopener noreferrer" className="font-display uppercase tracking-wide2 text-xs text-[var(--bone)]/85" data-testid={`nav-mobile-portfolio-${slug(p.label)}`}>
                    {p.label} ↗
                  </a>
                ) : (
                  <Link key={p.to} to={p.to} className="font-display uppercase tracking-wide2 text-xs text-[var(--bone)]/85" data-testid={`nav-mobile-portfolio-${slug(p.label)}`}>
                    {p.label}
                  </Link>
                )
              )}
            </div>
          </div>
          <Link to="/contact" className="font-display uppercase tracking-wide2 text-sm text-[var(--bone)]" data-testid="nav-mobile-contact">
            Contact
          </Link>
        </nav>
      )}
    </header>
  );
};
